import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { saveAs } from 'file-saver';
import TodoRegisterLayer from '../../../components/todo/TodoRegisterLayer';
import './WorkOrderCommentsTab.css';

const AVATAR_COLORS = [
  '#2196F3', '#9C27B0', '#FF9800', '#4CAF50', '#00BCD4',
  '#3F51B5', '#009688', '#FFC107', '#795548', '#607D8B'
];

const getAvatarColorInfo = (identifier) => {
  if (!identifier) return { bg: '#0d6efd', text: 'light' };
  const str = String(identifier);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(Math.imul(hash, 2654435761));
  const index = hash % AVATAR_COLORS.length;
  const color = AVATAR_COLORS[index];
  const isDarkText = ['#FFC107', '#FF9800', '#00BCD4', '#4CAF50'].includes(color);
  return { bg: color, text: isDarkText ? 'dark' : 'light' };
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const now = new Date();
  const diffSec = Math.floor((now - d) / 1000);
  if (diffSec < 60) return '방금 전';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}분 전`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}시간 전`;
  if (diffSec < 2592000) return `${Math.floor(diffSec / 86400)}일 전`;
  const pad = n => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const getFileIconClass = (fileName) => {
  if (!fileName) return 'fas fa-file-alt text-secondary';
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf': return 'fas fa-file-pdf text-danger';
    case 'doc': case 'docx': return 'fas fa-file-word text-primary';
    case 'xls': case 'xlsx': case 'csv': return 'fas fa-file-excel text-success';
    case 'ppt': case 'pptx': return 'fas fa-file-powerpoint text-warning';
    case 'hwp': case 'hwpx': return 'fas fa-file-alt text-info';
    case 'jpg': case 'jpeg': case 'png': case 'gif': case 'webp': return 'fas fa-file-image text-info';
    case 'zip': case 'rar': case '7z': return 'fas fa-file-archive text-secondary';
    default: return 'fas fa-file text-muted';
  }
};

const checkIsFile = (c) => {
  if (!c) return false;
  if (c.file || c.file_path || c.file_name) return true;
  return c.content && /\.(gif|jpg|jpeg|png|pdf|zip|doc|docx|xls|xlsx|ppt|pptx|txt|hwp|csv|ai|psd)$/i.test(String(c.content).trim());
};

export default function WorkOrderCommentsTab({
  workOrder,
  comments = [],
  newComment,
  setNewComment,
  selectedFiles = [],
  setSelectedFiles,
  fileInputRef,
  handleAddComment,
  handleDeleteComment,
  handleEditCommentSubmit,
  replyTargetId,
  setReplyTargetId,
  replyContent,
  setReplyContent,
  handleReplySubmit,
  currentUser,
  folders = [],
  onRefresh,
  focusId,
  isCc = false
}) {
  // ── 알림 클릭 시 포커스 스크롤 및 3초 펄스 깜빡임 상태 ──
  const commentsScrollContainerRef = useRef(null);
  const [highlightCommentId, setHighlightCommentId] = useState(null);
  const focusHandledRef = useRef(null);

  useEffect(() => {
    if (focusId && comments && comments.length > 0 && focusHandledRef.current !== String(focusId)) {
      let attempts = 0;
      const tryScroll = setInterval(() => {
        attempts++;
        const targetEl = document.getElementById(`comment-${focusId}`) || document.getElementById(`comment-item-${focusId}`) || document.getElementById(`feed-${focusId}`);
        const container = commentsScrollContainerRef.current;

        if (targetEl && container && container.clientHeight > 0) {
          clearInterval(tryScroll);
          requestAnimationFrame(() => {
            const contRect = container.getBoundingClientRect();
            const targetRect = targetEl.getBoundingClientRect();
            container.scrollTop = container.scrollTop + (targetRect.top - contRect.top) - (contRect.height / 2) + (targetRect.height / 2);
            focusHandledRef.current = String(focusId);
            setHighlightCommentId(String(focusId));
            setTimeout(() => {
              setHighlightCommentId(null);
            }, 3100);
          });
        }
        if (attempts >= 30) clearInterval(tryScroll);
      }, 100);

      return () => clearInterval(tryScroll);
    }
  }, [focusId, comments]);

  // 대댓글 등록 상태
  const [textReplyTargetId, setTextReplyTargetId] = useState(null);
  const [replyFiles, setReplyFiles] = useState([]);
  const replyFileInputRef = useRef(null);

  // 대댓글 수정 상태 (PMS 100% 동일)
  const [editTextCommentId, setEditTextCommentId] = useState(null);
  const [editTextCommentContent, setEditTextCommentContent] = useState('');

  // 코멘트 인라인 수정 상태 (PMS 100% 동일)
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editFiles, setEditFiles] = useState([]);
  const editFileInputRef = useRef(null);

  // 추가 파일 첨부 상태 (PMS 100% 동일)
  const [addingFileCommentId, setAddingFileCommentId] = useState(null);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const additionalFileInputRef = useRef(null);

  // 다중 파일 프리뷰 팝업 레이어 (PMS 100% 동일)
  const [activeFilePopup, setActiveFilePopup] = useState(null);
  const popupTimeoutRef = useRef(null);

  const handleFileLayerEnter = (e, source) => {
    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
    let files;
    if (source === 'selected') files = selectedFiles;
    else if (source === 'edit') files = editFiles;
    else if (source === 'additional') files = additionalFiles;
    else if (source === 'reply') files = replyFiles;

    if (!files || files.length <= 1) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const layerHeight = 260;

    setActiveFilePopup({
      source,
      rect,
      isTop: spaceBelow < layerHeight && rect.top > layerHeight
    });
  };

  const handleFileLayerLeave = () => {
    popupTimeoutRef.current = setTimeout(() => {
      setActiveFilePopup(null);
    }, 150);
  };

  const renderPortalPopup = () => {
    if (!activeFilePopup) return null;
    const { source, rect, isTop } = activeFilePopup;

    let files, filesSetter;
    if (source === 'selected') { files = selectedFiles; filesSetter = setSelectedFiles; }
    else if (source === 'edit') { files = editFiles; filesSetter = setEditFiles; }
    else if (source === 'additional') { files = additionalFiles; filesSetter = setAdditionalFiles; }
    else if (source === 'reply') { files = replyFiles; filesSetter = setReplyFiles; }

    if (!files || files.length <= 1) return null;

    const maxLeft = window.innerWidth - 320;
    const safeLeft = Math.min(rect.left, maxLeft);

    const popupStyle = {
      position: 'fixed',
      left: safeLeft,
      minWidth: '300px',
      zIndex: 2147483647,
      ...(isTop
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 })
    };

    const popupContent = (
      <div
        className={`premium-file-layer ${isTop ? 'popup-top' : ''}`}
        style={{ ...popupStyle, visibility: 'visible', opacity: 1, transform: 'translateY(0) scale(1)', pointerEvents: 'auto' }}
        onMouseEnter={() => { if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current); }}
        onMouseLeave={handleFileLayerLeave}
      >
        <div className="premium-file-layer-header fs-8 fw-bold text-muted d-flex justify-content-between align-items-center">
          <span>첨부된 파일 ({files.length})</span>
          <span style={{ cursor: 'pointer', fontSize: '11px', transition: 'opacity 0.2s' }}
            className="text-danger opacity-75"
            onMouseEnter={(e) => e.target.classList.replace('opacity-75', 'opacity-100')}
            onMouseLeave={(e) => e.target.classList.replace('opacity-100', 'opacity-75')}
            onClick={(e) => { e.stopPropagation(); filesSetter([]); setActiveFilePopup(null); }}>전체 삭제</span>
        </div>
        <div className="premium-file-layer-body" style={{ maxHeight: '200px', overflowY: 'auto', padding: '0 12px 8px 8px' }}>
          {files.map((f, idx) => (
            <div key={idx} className="d-flex justify-content-between align-items-center premium-file-item">
              <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                <div className="premium-file-icon me-2 flex-shrink-0">
                  <i className="fas fa-file-alt"></i>
                </div>
                <span className="text-truncate fw-bold text-dark flex-grow-1 premium-file-name" title={f.name}>{f.name}</span>
              </div>
              <div className="premium-delete-btn flex-shrink-0 ms-1" style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  const newFiles = files.filter((_, i) => i !== idx);
                  filesSetter(newFiles);
                  if (newFiles.length <= 1) setActiveFilePopup(null);
                }} title="삭제">
                <i className="fas fa-times" style={{ fontSize: '11px' }}></i>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
    return createPortal(popupContent, document.body);
  };

  // To-Do 등록 팝업 상태
  const [todoAnchorEl, setTodoAnchorEl] = useState(null);
  const [todoSource, setTodoSource] = useState(null);

  const handleOpenTodoRegister = (e, item) => {
    e.stopPropagation();
    setTodoAnchorEl({ current: e.currentTarget });

    let contentStr = '';
    let targetId = item?.id;
    if (item?.items && item.items.length > 0) {
      const textItem = item.items.find(i => i.content && !i.attached_file_id);
      contentStr = textItem ? textItem.content : '';
      targetId = item.id || item.items[0]?.id;
    } else if (item?.content && !item?.attached_file_id) {
      contentStr = item.content;
    }

    setTodoSource({
      ref_type: 'work_order_comment',
      ref_table: 'tdx_work_order_comments',
      ref_id: targetId,
      add_title: workOrder?.title || '업무협조',
      add_subtype: 'comment',
      add_id1: workOrder?.id,
      add_id2: targetId,
      title: `[업무협조 코멘트] ${workOrder?.title || ''}`,
      memo: contentStr
    });
  };

  // 피드(general, request, notice, system 등) 전면 차단: 순수 코멘트 및 파일만 표출
  const discussionComments = comments.filter(c => {
    if (['general', 'request', 'feed', 'notice', 'system'].includes(c.comment_type) || c.source_tab === 'feedback') {
      return false;
    }
    if (checkIsFile(c)) return true;
    return c.comment_type === 'comment' || !c.comment_type;
  });

  // PMS 100% 동일 그룹핑 로직 (작성자 + 작성시간 기준 그룹핑)
  const groupComments = (commentList) => {
    if (!commentList || commentList.length === 0) return [];
    const rootCommentsOnly = commentList.filter(c => !c.parent_id || c.parent_id === 0);
    if (rootCommentsOnly.length === 0) return [];

    const sorted = [...rootCommentsOnly].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    const grouped = [];
    let currentGroup = { ...sorted[0], items: [sorted[0]] };

    for (let i = 1; i < sorted.length; i++) {
      const c = sorted[i];
      if (c.user_id === currentGroup.user_id && c.created_at === currentGroup.created_at) {
        currentGroup.items.push(c);
      } else {
        grouped.push(currentGroup);
        currentGroup = { ...c, items: [c] };
      }
    }
    grouped.push(currentGroup);
    return grouped;
  };

  // PMS 100% 동일 재귀 대댓글 렌더링 함수
  const renderTextReplies = (rootParentId) => {
    const getAllDescendants = (parentId) => {
      let result = [];
      const children = discussionComments.filter(c => String(c.parent_id) === String(parentId) && !checkIsFile(c));
      for (const child of children) {
        result.push(child);
        result = result.concat(getAllDescendants(child.id));
      }
      return result;
    };

    let allReplies = getAllDescendants(rootParentId);
    if (allReplies.length === 0) return null;
    allReplies.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    return (
      <div className="d-flex flex-column" style={{ paddingLeft: '8px' }}>
        {allReplies.map((reply, idx) => {
          const replyAuthorId = reply.user_id || reply.user?.id || reply.user?.userid;
          const replyName = reply.user?.name || '사용자';
          const replyPos = reply.user?.org?.position || reply.user?.position ? ` ${reply.user?.org?.position || reply.user?.position}` : '';
          const replyDept = reply.user?.org?.dept_name || reply.user?.dept_name ? ` [${reply.user?.org?.dept_name || reply.user?.dept_name}]` : '';
          const replyFullName = `${replyName}${replyPos}${replyDept}`;

          const avatarInfo = getAvatarColorInfo(replyAuthorId || replyName);
          const photoPath = reply.user?.org?.photo_path || reply.user?.photo_path;

          return (
            <div key={`text-reply-${reply.id}`} id={`comment-${reply.id}`} className={`py-1 position-relative d-flex ${idx > 0 ? 'mt-1' : ''} action-container animation-fade-in ${highlightCommentId === String(reply.id) ? 'highlight-comment' : ''}`}>
              {/* L자 꺾쇠 연결선 */}
              <div className="reply-connector" style={{ left: '-12px', width: '12px' }} />

              <div
                className="rounded-circle d-flex align-items-center justify-content-center shadow-sm fw-bold fs-9 overflow-hidden flex-shrink-0"
                style={{
                  width: '22px',
                  height: '22px',
                  backgroundColor: photoPath ? 'transparent' : avatarInfo.bg,
                  color: avatarInfo.text === 'dark' ? '#333' : '#fff',
                  marginTop: '2px',
                  zIndex: 1
                }}
              >
                {photoPath ? (
                  <img src={photoPath} alt={replyName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  replyName.charAt(0)
                )}
              </div>

              <div className="ms-2 flex-grow-1 min-w-0">
                <div className="d-flex align-items-center mb-1 flex-wrap">
                  <span className="fw-bold text-dark fs-8 me-2">{replyFullName}</span>
                  <span className="text-muted" style={{ fontSize: '11px' }}>{formatDateTime(reply.created_at)}</span>

                  <div className="d-flex gap-1 align-items-center opacity-0 action-buttons transition-opacity ms-2 flex-shrink-0">
                    {!isCc && (
                      <button
                        type="button"
                        className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded"
                        style={{ background: 'transparent' }}
                        title={`${replyName}님에게 답글 달기`}
                        onClick={() => {
                          setTextReplyTargetId(reply.id);
                          setReplyContent('');
                        }}
                      >
                        <i className="fas fa-reply fs-9" />
                      </button>
                    )}
                    {!isCc && (currentUser?.id === reply.user_id || currentUser?.id === workOrder?.author_id || currentUser?.role === 'admin' || currentUser?.isAdmin) && editTextCommentId !== reply.id && (
                      <button
                        type="button"
                        className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded"
                        style={{ background: 'transparent' }}
                        title="수정"
                        onClick={() => {
                          setEditTextCommentId(reply.id);
                          setEditTextCommentContent(reply.content || '');
                        }}
                      >
                        <i className="fas fa-edit fs-9" />
                      </button>
                    )}
                    {!isCc && (currentUser?.id === reply.user_id || currentUser?.id === workOrder?.author_id || currentUser?.role === 'admin' || currentUser?.isAdmin) && (
                      <button
                        type="button"
                        className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded"
                        style={{ background: 'transparent' }}
                        title="삭제"
                        onClick={() => handleDeleteComment && handleDeleteComment(reply.id)}
                      >
                        <i className="fas fa-trash-alt fs-9" />
                      </button>
                    )}
                  </div>
                </div>

                {/* 답글 본문 또는 인라인 수정 폼 */}
                {editTextCommentId === reply.id ? (
                  <div className="mt-1 d-flex gap-2 align-items-stretch">
                    <textarea
                      className="form-control form-control-sm shadow-none"
                      rows="2"
                      value={editTextCommentContent}
                      onChange={(e) => setEditTextCommentContent(e.target.value)}
                      style={{ resize: 'none', fontSize: '12px', border: '1px solid #ced4da', flexGrow: 1 }}
                      autoFocus
                    />
                    <div className="d-flex flex-column gap-1 justify-content-center flex-shrink-0">
                      <button
                        type="button"
                        className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1"
                        style={{ backgroundColor: '#4c6ef5', fontSize: '10px', lineHeight: '1' }}
                        onClick={async () => {
                          if (handleEditCommentSubmit) {
                            const success = await handleEditCommentSubmit(reply.id, editTextCommentContent);
                            if (success) setEditTextCommentId(null);
                          }
                        }}
                      >
                        저장
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1"
                        style={{ backgroundColor: '#868e96', fontSize: '10px', lineHeight: '1' }}
                        onClick={() => { setEditTextCommentId(null); setEditTextCommentContent(''); }}
                      >
                        취소
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-dark custom-scrollbar w-100" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px', lineHeight: '1.4' }}>
                    {reply.target_mention && <span className="text-primary fw-bold me-1">@{reply.target_mention}</span>}
                    {reply.content}
                  </div>
                )}

                {textReplyTargetId === reply.id && (
                  <div className="mt-2 pt-2" style={{ borderTop: '1px dashed rgba(0,0,0,0.08)' }}>
                    <div className="d-flex gap-2 align-items-stretch">
                      <textarea
                        className="form-control form-control-sm shadow-none"
                        rows="2"
                        placeholder={`@${replyName}님에게 텍스트 답글...`}
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        style={{ resize: 'none', fontSize: '12px', border: '1px solid #ced4da', flexGrow: 1 }}
                        autoFocus
                      />
                      <div className="d-flex flex-column gap-1 justify-content-center flex-shrink-0">
                        <button
                          type="button"
                          className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1"
                          style={{ backgroundColor: '#4c6ef5', fontSize: '10px', lineHeight: '1' }}
                          onClick={() => {
                            if (handleReplySubmit) {
                              handleReplySubmit(reply.id, replyContent, [], replyName);
                            }
                            setTextReplyTargetId(null);
                            setReplyContent('');
                          }}
                        >
                          저장
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1"
                          style={{ backgroundColor: '#868e96', fontSize: '10px', lineHeight: '1' }}
                          onClick={() => { setTextReplyTargetId(null); setReplyContent(''); }}
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const stripUniqueKey = (name) => {
    if (!name) return name;
    return name.replace(/_[0-9]{8}F[0-9]{6}(?=\.[^.]+$|$)/g, '');
  };

  const handleDownloadFile = async (fileObj) => {
    if (!fileObj) {
      alert('다운로드할 파일 정보가 없습니다.');
      return;
    }
    const rawPath = fileObj.file_path || fileObj.path || (fileObj.file_name ? `/uploads/techni/workOrder/${fileObj.file_name}` : null) || (fileObj.originalName ? `/uploads/techni/workOrder/${fileObj.originalName}` : null);
    if (!rawPath) {
      alert('다운로드할 파일 정보가 없습니다.');
      return;
    }
    const downloadPath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
    try {
      const response = await fetch(downloadPath);
      const blob = await response.blob();
      saveAs(blob, stripUniqueKey(fileObj.file_name || fileObj.originalName || 'download'));
    } catch (e) {
      console.error('다운로드 실패:', e);
      alert('파일 다운로드 중 오류가 발생했습니다.');
    }
  };

  // 동일 레벨 중복 파일명 및 미선택 검증 함수
  const validateAttachmentFiles = (targetFiles, parentId = null, existingItemsAtLevel = null) => {
    // 1. 파일 미선택 검사
    if (!targetFiles || targetFiles.length === 0) {
      alert('첨부할 파일을 선택해 주세요.');
      return false;
    }

    // 2. 선택한 파일 목록 자체 내 중복 검사
    const selectedNames = targetFiles.map(f => stripUniqueKey(f.name || ''));
    const uniqueSelected = new Set();
    for (const name of selectedNames) {
      if (uniqueSelected.has(name)) {
        alert(`선택한 파일 목록에 중복된 파일명이 포함되어 있습니다.\n파일명: ${name}`);
        return false;
      }
      uniqueSelected.add(name);
    }

    // 3. 현재 위치(동일 레벨) 기존 파일들과의 중복 검사
    let existingNames = [];
    if (existingItemsAtLevel && Array.isArray(existingItemsAtLevel)) {
      existingNames = existingItemsAtLevel
        .filter(item => checkIsFile(item) || item.file)
        .map(item => stripUniqueKey(item.file?.originalName || item.file?.file_name || item.original_file_name || item.content || ''));
    } else if (parentId) {
      existingNames = discussionComments
        .filter(c => String(c.parent_id) === String(parentId) && (checkIsFile(c) || c.file))
        .map(c => stripUniqueKey(c.file?.originalName || c.file?.file_name || c.original_file_name || c.content || ''));
    }

    for (const name of selectedNames) {
      if (existingNames.includes(name)) {
        alert(`현재 위치(동일 레벨)에 이미 동일한 이름의 파일이 존재합니다.\n파일명: ${name}`);
        return false;
      }
    }

    return true;
  };

  // PMS 100% 동일 첨부파일 및 하위 답글 계층 렌더링 함수
  const renderReplies = (parentId, rootAuthorId = null, depth = 0) => {
    const childReplies = discussionComments.filter(c => String(c.parent_id) === String(parentId));
    if (childReplies.length === 0) return null;

    const sortedReplies = [...childReplies].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return sortedReplies.map(reply => {
      const isReplyFile = checkIsFile(reply);
      const replyAuthorId = reply.user_id || reply.user?.id || reply.user?.userid;
      const isSameAuthor = rootAuthorId && String(replyAuthorId) === String(rootAuthorId);
      const replyName = reply.user?.name || '알 수 없음';
      const replyPos = reply.user?.org?.position || reply.user?.position ? ` ${reply.user?.org?.position || reply.user?.position}` : '';
      const replyDept = reply.user?.org?.dept_name || reply.user?.dept_name ? ` [${reply.user?.org?.dept_name || reply.user?.dept_name}]` : '';
      const replyFullName = `${replyName}${replyPos}${replyDept}`;

      const isReplyHighlighted = highlightCommentId && String(reply.id) === String(highlightCommentId);

      return (
        <div id={`comment-${reply.id}`} key={reply.id} className={`mt-1 mb-1 animation-fade-in ${isReplyHighlighted ? 'highlight-comment p-1' : ''}`} style={{ marginLeft: `${depth * 15}px` }}>
          {isReplyFile ? (
            <div className="d-flex align-items-center action-container">
              <i className="fas fa-level-up-alt fa-rotate-90 text-secondary me-2 fs-6 flex-shrink-0" />
              <i className={`${getFileIconClass(stripUniqueKey(reply.file?.originalName || reply.content))} fa-fw me-2 flex-shrink-0`} style={{ fontSize: '18px', verticalAlign: 'middle' }} />
              <span
                className="text-dark fw-bold fs-7 me-2 text-truncate"
                style={{ cursor: 'pointer', maxWidth: `calc(min(350px, 20vw) - ${depth * 15}px)` }}
                onClick={() => handleDownloadFile(reply.file)}
                onMouseEnter={(e) => {
                  if (e.target.offsetWidth < e.target.scrollWidth) {
                    e.target.title = stripUniqueKey(reply.content);
                  } else {
                    e.target.title = '';
                  }
                }}
              >
                {stripUniqueKey(reply.content)}
              </span>
              <span className="text-muted fs-8 me-3 flex-shrink-0">[{Math.round((reply.file?.file_size || 1024) / 1024)}kb]</span>

              <span className="text-secondary fs-7 flex-shrink-0">
                {isSameAuthor ? formatDateTime(reply.created_at) : (
                  <>{replyFullName} <span className="text-muted mx-1">|</span> {formatDateTime(reply.created_at)}</>
                )}
              </span>

              <div className="d-flex gap-1 align-items-center opacity-0 action-buttons transition-opacity ms-2 flex-shrink-0">
                <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="다운로드" onClick={() => handleDownloadFile(reply.file)}>
                  <i className="fas fa-download" />
                </button>
                <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="답글(파일첨부)" onClick={() => setReplyTargetId(reply.id)}>
                  <i className="fas fa-plus" />
                </button>
                {(currentUser?.id === reply.user_id || currentUser?.id === workOrder?.author_id || currentUser?.role === 'admin' || currentUser?.isAdmin) && (
                  <button className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="삭제" onClick={() => handleDeleteComment && handleDeleteComment(reply.id)}>
                    <i className="fas fa-trash-alt" />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center">
                <i className="fas fa-level-up-alt fa-rotate-90 text-secondary me-2 fs-6 flex-shrink-0" />
                <span className="text-secondary fs-7 flex-shrink-0">
                  {isSameAuthor ? formatDateTime(reply.created_at) : (
                    <>{replyFullName} <span className="text-muted mx-1">|</span> {formatDateTime(reply.created_at)}</>
                  )}
                </span>
              </div>
              <div className="action-container position-relative w-100 mt-1">
                <div className="text-dark fs-7 custom-scrollbar w-100" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '84px', overflowY: 'auto' }}>{reply.content}</div>
                <div className="position-absolute d-flex gap-1 align-items-center opacity-0 action-buttons transition-opacity" style={{ right: '-5px', top: '0px' }}>
                  <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="To-Do 등록" onClick={(e) => handleOpenTodoRegister(e, reply)}>
                    <i className="fas fa-tasks" />
                  </button>
                  <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="답글(파일첨부)" onClick={() => setReplyTargetId(reply.id)}>
                    <i className="fas fa-plus" />
                  </button>
                  {(currentUser?.id === reply.user_id || currentUser?.id === workOrder?.author_id || currentUser?.role === 'admin' || currentUser?.isAdmin) && (
                    <button className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="삭제" onClick={() => handleDeleteComment && handleDeleteComment(reply.id)}>
                      <i className="fas fa-trash-alt" />
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {replyTargetId === reply.id && (
            <div className="mt-2 mb-2 d-flex align-items-center gap-2 animation-fade-in">
              <div className="d-flex bg-white border flex-grow-1 align-items-center" style={{ height: '28px' }}>
                <input
                  type="text"
                  className="form-control border-0 shadow-none h-100 flex-grow-1 rounded-0 fs-8 px-2 bg-white"
                  placeholder={replyFiles.length > 0 ? `${replyFiles[0].name}${replyFiles.length > 1 ? ` 외 ${replyFiles.length - 1}개` : ''}` : "첨부할 파일을 선택하세요..."}
                  readOnly
                  onClick={() => replyFileInputRef.current?.click()}
                  style={{ cursor: 'pointer' }}
                />
                <input type="file" className="d-none" ref={replyFileInputRef} multiple onChange={(e) => setReplyFiles(Array.from(e.target.files))} />
                <button className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center" title="파일 첨부" onClick={() => replyFileInputRef.current?.click()}>
                  <i className="fas fa-upload fs-8" />
                </button>
              </div>
              <button
                type="button"
                className="btn btn-sm text-white rounded-1 px-2 fw-bold fs-8 shadow-sm"
                style={{ backgroundColor: '#4c6ef5', height: '28px' }}
                onClick={() => {
                  if (!validateAttachmentFiles(replyFiles, reply.id)) return;
                  if (handleReplySubmit) {
                    handleReplySubmit(reply.id, replyContent, replyFiles, replyName);
                  }
                  setReplyFiles([]);
                  setReplyContent('');
                  setReplyTargetId(null);
                }}
              >
                저장
              </button>
              <button
                type="button"
                className="btn btn-sm text-white rounded-1 px-2 fw-bold fs-8 shadow-sm"
                style={{ backgroundColor: '#868e96', height: '28px' }}
                onClick={() => {
                  setReplyTargetId(null);
                  setReplyFiles([]);
                  setReplyContent('');
                }}
              >
                취소
              </button>
            </div>
          )}

          {renderReplies(reply.id, rootAuthorId, depth + 1)}
        </div>
      );
    });
  };

  const grouped = groupComments(discussionComments);

  return (
    <div className="animation-fade-in d-flex flex-column h-100 p-0" style={{ width: '100%' }}>
      {/* ── 상단 신규 코멘트 등록 박스 (참조자는 숨김) ── */}
      {!isCc && (
        <div className="bg-white p-3 rounded shadow-sm mb-3 border border-light new-comment-container position-relative flex-shrink-0" style={{ zIndex: 1001 }}>
          <textarea
            className="form-control border-0 shadow-none bg-light mb-2 light-placeholder"
            placeholder="작업 진행 상황이나 코멘트를 남겨보세요."
            rows="3"
            style={{ resize: 'none', fontSize: '0.85rem' }}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />

          <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
            <input
              type="file"
              className="d-none"
              ref={fileInputRef}
              multiple
              onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
            />

            <div className="d-flex align-items-center flex-grow-1 min-w-0 me-3">
              <button
                type="button"
                className="btn btn-light text-secondary d-inline-flex align-items-center px-3 border-0 rounded-pill flex-shrink-0"
                style={{ fontSize: '0.9rem', height: '36px' }}
                onClick={() => fileInputRef.current?.click()}
                title="파일 첨부"
              >
                <i className="fas fa-paperclip me-2" />첨부
              </button>

              <div
                className={`file-container d-flex align-items-center rounded-pill px-3 ms-2 flex-grow-1 min-w-0 ${selectedFiles.length > 0 ? 'bg-white' : ''}`}
                style={{
                  height: '36px',
                  border: selectedFiles.length > 0 ? '1px solid #dee2e6' : '1px dashed #ced4da',
                  backgroundColor: selectedFiles.length > 0 ? '#ffffff' : '#f8f9fa'
                }}
              >
                <span
                  className={`text-truncate ${selectedFiles.length > 0 ? 'text-secondary' : 'text-muted'}`}
                  style={{ fontSize: '0.85rem', cursor: selectedFiles.length > 1 ? 'pointer' : 'default' }}
                  onMouseEnter={(e) => handleFileLayerEnter(e, 'selected')}
                  onMouseLeave={handleFileLayerLeave}
                >
                  {selectedFiles.length > 0
                    ? `${selectedFiles[0].name}${selectedFiles.length > 1 ? ` 외 ${selectedFiles.length - 1}개` : ''}`
                    : '첨부된 파일이 없습니다.'}
                </span>
                {selectedFiles.length > 0 && (
                  <i
                    className="fas fa-times text-muted ms-auto flex-shrink-0 cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedFiles([]);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    title="첨부 전체 취소"
                  />
                )}
              </div>
            </div>

            <button
              type="button"
              className="btn btn-primary fw-bold px-4 rounded-pill shadow-sm flex-shrink-0"
              onClick={handleAddComment}
            >
              등록
            </button>
          </div>
        </div>
      )}

      {/* ── 댓글 스레드 목록 영역 (PMS 100% 동일) ── */}
      <div ref={commentsScrollContainerRef} className="flex-grow-1 overflow-auto bg-white py-2 px-3 rounded-3 shadow-sm border" style={{ borderColor: '#dee2e6' }}>
        {grouped.length === 0 ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 mt-4">
            <div className="rounded-circle d-flex align-items-center justify-content-center mb-3 shadow-sm" style={{ width: '80px', height: '80px', backgroundColor: '#eef2ff' }}>
              <i className="far fa-comments text-primary" style={{ fontSize: '2.2rem' }} />
            </div>
            <h6 className="fw-bold text-dark mb-2">등록된 코멘트나 파일이 없습니다</h6>
            <p className="text-muted fs-7 mb-0 text-center">작업 진행 상황이나 피드백을 자유롭게 남기고<br />팀원들과 원활하게 소통해 보세요!</p>
          </div>
        ) : (
          <div className="position-relative ms-3 mt-3 pb-1" style={{ paddingLeft: '16px' }}>
            {grouped.map((group, index, arr) => {
              const isLast = index === arr.length - 1;
              const authorName = group.user?.name || '사용자';
              const authorPosition = group.user?.org?.position ? ` ${group.user.org.position}` : (group.user?.position ? ` ${group.user.position}` : '');
              const authorDept = group.user?.org?.dept_name ? ` [${group.user.org.dept_name}]` : (group.user?.dept_name ? ` [${group.user.dept_name}]` : '');
              const authorFullName = `${authorName}${authorPosition}${authorDept}`;
              const avatarInfo = getAvatarColorInfo(group.user?.id || group.user?.userid || authorName);
              const photoPath = group.user?.org?.photo_path || group.user?.photo_path;

              // 텍스트 아이템 존재 여부
              const hasTextItem = group.items.some(i => !checkIsFile(i) && i.content);
              const isOwnerOrAuthor = currentUser?.id === group.user_id || currentUser?.id === workOrder?.author_id || currentUser?.role === 'admin' || currentUser?.isAdmin;

              const isGroupHighlighted = highlightCommentId && (
                String(group.id) === String(highlightCommentId) ||
                group.items.some(it => String(it.id) === String(highlightCommentId))
              );

              return (
                <div id={`comment-${group.id}`} key={group.id} className={`mb-3 position-relative comment-group-container ${isGroupHighlighted ? 'highlight-comment' : ''}`} style={{ paddingLeft: '28px', zIndex: 1000 - index }}>
                  {/* 세로 회색 가이드 라인 (PMS 100% 동일) */}
                  {!isLast && (
                    <div className="position-absolute bg-secondary bg-opacity-25" style={{ width: '2px', top: '36px', bottom: '-14px', left: '0px', zIndex: 0 }} />
                  )}

                  {/* 외부 원형 프로필 아바타 (PMS 100% 동일) */}
                  <div className="position-absolute avatar-wrapper" style={{ left: '-15px', top: '4px', zIndex: 10 }}>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center shadow-sm fw-bold fs-7 overflow-hidden"
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: photoPath ? 'transparent' : avatarInfo.bg,
                        color: avatarInfo.text === 'dark' ? '#333' : '#fff'
                      }}
                    >
                      {photoPath ? (
                        <img src={photoPath} alt={authorName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        authorName.charAt(0)
                      )}
                    </div>
                  </div>

                  {/* 댓글 본문 카드 (PMS 100% 동일) */}
                  <div className="bg-white rounded-4 shadow-sm position-relative w-100 border border-secondary border-opacity-10" style={{ borderTopLeftRadius: '4px' }}>
                    <div className="action-container">
                      {/* 카드 상단 회색 헤더 */}
                      <div className="ps-3 pe-2 py-1 border-bottom border-secondary border-opacity-10 bg-light d-flex align-items-center" style={{ borderTopRightRadius: '0.5rem', borderTopLeftRadius: '4px' }}>
                        <span className="fw-bold text-dark fs-7 me-2">{authorFullName}</span>
                        <span className="text-muted fs-8">{formatDateTime(group.created_at)}</span>

                        <div className="d-flex ms-auto flex-shrink-0 gap-1 opacity-0 action-buttons transition-opacity">
                          <button
                            type="button"
                            className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded"
                            style={{ background: 'transparent' }}
                            title="To-Do 등록"
                            onClick={(e) => handleOpenTodoRegister(e, group)}
                          >
                            <i className="fas fa-tasks" />
                          </button>
                          {!isCc && (
                            <button
                              type="button"
                              className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded"
                              style={{ background: 'transparent' }}
                              title="추가 첨부"
                              onClick={() => {
                                setAddingFileCommentId(addingFileCommentId === group.id ? null : group.id);
                                setAdditionalFiles([]);
                              }}
                            >
                              <i className="fas fa-plus" />
                            </button>
                          )}
                          {!isCc && hasTextItem && (
                            <button
                              type="button"
                              className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded"
                              style={{ background: 'transparent' }}
                              title={`${authorName}님에게 답글 달기`}
                              onClick={() => {
                                const textItem = group.items.find(i => !checkIsFile(i));
                                if (textItem) {
                                  setTextReplyTargetId(textItem.id);
                                  setReplyContent('');
                                }
                              }}
                            >
                              <i className="fas fa-reply" />
                            </button>
                          )}
                          {!isCc && isOwnerOrAuthor && editingCommentId !== group.id && (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded"
                                style={{ background: 'transparent' }}
                                title="수정"
                                onClick={() => {
                                  const textItem = group.items.find(i => !checkIsFile(i));
                                  setEditingCommentId(group.id);
                                  setEditContent(textItem ? textItem.content : '');
                                  setEditFiles([]);
                                }}
                              >
                                <i className="fas fa-edit" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded"
                                style={{ background: 'transparent' }}
                                title="삭제"
                                onClick={() => {
                                  const mainItem = group.items.find(i => !checkIsFile(i)) || group.items[0];
                                  if (mainItem && handleDeleteComment) handleDeleteComment(mainItem.id);
                                }}
                              >
                                <i className="fas fa-trash-alt" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* 댓글 본문 및 인라인 수정 영역 */}
                      <div className="d-flex flex-column">
                        {editingCommentId === group.id ? (
                          /* 인라인 수정 폼 (PMS 100% 동일) - 파일만 등록된 카드에서도 텍스트를 새로 작성할 수 있음! */
                          <div className="px-3 py-2 d-flex flex-column gap-2 bg-light border-bottom animation-fade-in">
                            <textarea
                              className="form-control form-control-sm shadow-none"
                              value={editContent}
                              onChange={e => setEditContent(e.target.value)}
                              rows="2"
                              placeholder="코멘트 내용을 입력하세요..."
                              style={{ resize: 'none', fontSize: '13px' }}
                              autoFocus
                            />
                            <div className="d-flex align-items-center gap-2">
                              <div className="d-flex bg-white border flex-grow-1 align-items-center file-container position-relative" style={{ height: '30px', minWidth: 0, borderRadius: '4px' }}>
                                <div
                                  className="form-control border-0 shadow-none h-100 flex-grow-1 rounded-0 px-2 bg-white d-flex align-items-center cursor-pointer"
                                  onClick={() => editFileInputRef.current?.click()}
                                  style={{ minWidth: 0, fontSize: '11px' }}
                                >
                                  <span className={editFiles.length > 0 ? "text-dark text-truncate fw-bold" : "text-muted text-truncate"}>
                                    {editFiles.length > 0 ? `${editFiles[0].name}${editFiles.length > 1 ? ` 외 ${editFiles.length - 1}개` : ''}` : "추가할 파일을 선택하세요..."}
                                  </span>
                                </div>
                                {editFiles.length > 0 && (
                                  <i
                                    className="fas fa-times text-muted px-2 border-start d-flex align-items-center justify-content-center h-100 flex-shrink-0 cursor-pointer"
                                    style={{ width: '32px' }}
                                    onClick={() => setEditFiles([])}
                                    title="전체 삭제"
                                  />
                                )}
                                <input
                                  type="file"
                                  className="d-none"
                                  ref={editFileInputRef}
                                  multiple
                                  onChange={(e) => setEditFiles(Array.from(e.target.files))}
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                  title="파일 첨부"
                                  onClick={() => editFileInputRef.current?.click()}
                                >
                                  <i className="fas fa-upload" style={{ fontSize: '11px' }} />
                                </button>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-shrink-0"
                                style={{ backgroundColor: '#4c6ef5', height: '30px', fontSize: '11px' }}
                                onClick={async () => {
                                  if (editFiles.length > 0 && !validateAttachmentFiles(editFiles, null, group.items)) return;
                                  if (handleEditCommentSubmit) {
                                    const textItem = group.items.find(i => !checkIsFile(i));
                                    const success = await handleEditCommentSubmit(textItem ? textItem.id : null, editContent, editFiles, group.created_at);
                                    if (success) {
                                      setEditingCommentId(null);
                                      setEditFiles([]);
                                      setEditContent('');
                                    }
                                  }
                                }}
                              >
                                저장
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-shrink-0"
                                style={{ backgroundColor: '#868e96', height: '30px', fontSize: '11px' }}
                                onClick={() => { setEditingCommentId(null); setEditFiles([]); setEditContent(''); }}
                              >
                                취소
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {group.items.filter(item => !checkIsFile(item)).map(item => (
                              <React.Fragment key={`text-wrap-${item.id}`}>
                                <div className="text-dark lh-base ps-3 pe-2 position-relative d-flex align-items-center" style={{ paddingTop: '8px', paddingBottom: '8px', fontSize: '13px' }}>
                                  <div className="custom-scrollbar flex-grow-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowY: 'auto' }}>
                                    {item.content}
                                  </div>
                                </div>

                                {/* 텍스트 답글 및 인라인 답글 입력 영역 */}
                                {(() => {
                                  const hasTextReplies = discussionComments.some(c => String(c.parent_id) === String(item.id) && !checkIsFile(c));
                                  const isReplying = !isCc && textReplyTargetId === item.id;
                                  if (!hasTextReplies && !isReplying) return null;

                                  return (
                                    <div className="py-2 pe-3" style={{ paddingLeft: '32px', borderTop: '1px solid rgba(0,0,0,0.06)', backgroundColor: '#fdfdfd' }}>
                                      {hasTextReplies && renderTextReplies(item.id)}

                                      {isReplying && (
                                        <div className="mt-2 pt-2" style={{ borderTop: '1px dashed rgba(0,0,0,0.08)' }}>
                                          <div className="d-flex gap-2 align-items-stretch">
                                            <textarea
                                              className="form-control form-control-sm shadow-none"
                                              rows="2"
                                              placeholder={`@${authorName}님에게 텍스트 답글...`}
                                              value={replyContent}
                                              onChange={(e) => setReplyContent(e.target.value)}
                                              style={{ resize: 'none', fontSize: '12px', border: '1px solid #ced4da', flexGrow: 1 }}
                                              autoFocus
                                            />
                                            <div className="d-flex flex-column gap-1 justify-content-center flex-shrink-0">
                                              <button
                                                type="button"
                                                className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1"
                                                style={{ backgroundColor: '#4c6ef5', fontSize: '10px', lineHeight: '1' }}
                                                onClick={() => {
                                                  if (handleReplySubmit) {
                                                    handleReplySubmit(item.id, replyContent, [], authorName);
                                                  }
                                                  setTextReplyTargetId(null);
                                                  setReplyContent('');
                                                }}
                                              >
                                                저장
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1"
                                                style={{ backgroundColor: '#868e96', fontSize: '10px', lineHeight: '1' }}
                                                onClick={() => { setTextReplyTargetId(null); setReplyContent(''); }}
                                              >
                                                취소
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* 첨부파일 답글 렌더링 영역 (PMS 100% 동일) */}
                                {replyTargetId === item.id && (
                                  <div className="bg-light border-top border-secondary border-opacity-10 d-flex flex-column py-1">
                                    <div className="mt-1 mx-3 mb-1 d-flex align-items-center gap-2 animation-fade-in">
                                      <i className="fas fa-level-up-alt fa-rotate-90 text-secondary ms-1 fs-5" />
                                      <div className="d-flex bg-white border flex-grow-1 align-items-center file-container position-relative" style={{ height: '32px', minWidth: 0 }}>
                                        <div
                                          className="form-control border-0 shadow-none h-100 flex-grow-1 rounded-0 fs-8 px-2 bg-white d-flex align-items-center cursor-pointer"
                                          onClick={() => replyFileInputRef.current?.click()}
                                          style={{ minWidth: 0 }}
                                        >
                                          <span
                                            className={replyFiles.length > 0 ? "text-dark text-truncate fw-bold" : "text-muted text-truncate"}
                                            style={{ cursor: replyFiles.length > 1 ? 'pointer' : 'default' }}
                                            onMouseEnter={(e) => handleFileLayerEnter(e, 'reply')}
                                            onMouseLeave={handleFileLayerLeave}
                                          >
                                            {replyFiles.length > 0 ? `${replyFiles[0].name}${replyFiles.length > 1 ? ` 외 ${replyFiles.length - 1}개` : ''}` : "첨부할 파일을 선택하세요..."}
                                          </span>
                                        </div>
                                        {replyFiles.length > 0 && (
                                          <i
                                            className="fas fa-times text-muted px-2 border-start d-flex align-items-center justify-content-center h-100 flex-shrink-0 cursor-pointer"
                                            style={{ cursor: 'pointer', width: '32px' }}
                                            onClick={() => setReplyFiles([])}
                                            title="전체 삭제"
                                          />
                                        )}
                                        <input
                                          type="file"
                                          className="d-none"
                                          ref={replyFileInputRef}
                                          multiple
                                          onChange={(e) => setReplyFiles(Array.from(e.target.files))}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                          title="파일 첨부"
                                          onClick={() => replyFileInputRef.current?.click()}
                                        >
                                          <i className="fas fa-upload" />
                                        </button>
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-shrink-0"
                                        style={{ backgroundColor: '#4c6ef5', height: '32px' }}
                                        onClick={() => {
                                          if (!validateAttachmentFiles(replyFiles, item.id)) return;
                                          if (handleReplySubmit) {
                                            handleReplySubmit(item.id, replyContent, replyFiles, authorName);
                                          }
                                          setReplyFiles([]);
                                          setReplyContent('');
                                          setReplyTargetId(null);
                                        }}
                                      >
                                        저장
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-shrink-0"
                                        style={{ backgroundColor: '#868e96', height: '32px' }}
                                        onClick={() => {
                                          setReplyTargetId(null);
                                          setReplyFiles([]);
                                          setReplyContent('');
                                        }}
                                      >
                                        취소
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </React.Fragment>
                            ))}
                          </>
                        )}

                        {/* 추가 첨부 인풋 바 (PMS 100% 동일) */}
                        {addingFileCommentId === group.id && (
                          <div className="px-3 py-2 d-flex align-items-center gap-2 bg-light border-top border-bottom animation-fade-in">
                            <div className="d-flex bg-white border flex-grow-1 align-items-center file-container position-relative" style={{ height: '30px', minWidth: 0, borderRadius: '4px' }}>
                              <div
                                className="form-control border-0 shadow-none h-100 flex-grow-1 rounded-0 px-2 bg-white d-flex align-items-center cursor-pointer"
                                onClick={() => additionalFileInputRef.current?.click()}
                                style={{ minWidth: 0, fontSize: '11px' }}
                              >
                                <span className={additionalFiles.length > 0 ? "text-dark text-truncate fw-bold" : "text-muted text-truncate"}>
                                  {additionalFiles.length > 0 ? `${additionalFiles[0].name}${additionalFiles.length > 1 ? ` 외 ${additionalFiles.length - 1}개` : ''}` : "추가할 파일을 선택하세요..."}
                                </span>
                              </div>
                              {additionalFiles.length > 0 && (
                                <i
                                  className="fas fa-times text-muted px-2 border-start d-flex align-items-center justify-content-center h-100 flex-shrink-0 cursor-pointer"
                                  onClick={() => setAdditionalFiles([])}
                                  title="전체 삭제"
                                />
                              )}
                              <input
                                type="file"
                                className="d-none"
                                ref={additionalFileInputRef}
                                multiple
                                onChange={(e) => setAdditionalFiles(Array.from(e.target.files))}
                              />
                              <button
                                type="button"
                                className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                title="파일 선택"
                                onClick={() => additionalFileInputRef.current?.click()}
                              >
                                <i className="fas fa-upload" style={{ fontSize: '11px' }} />
                              </button>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-shrink-0"
                              style={{ backgroundColor: '#4c6ef5', height: '30px', fontSize: '11px' }}
                              onClick={async () => {
                                if (!validateAttachmentFiles(additionalFiles, null, group.items)) return;
                                if (handleEditCommentSubmit) {
                                  const success = await handleEditCommentSubmit(null, '', additionalFiles, group.created_at);
                                  if (success) {
                                    setAddingFileCommentId(null);
                                    setAdditionalFiles([]);
                                  }
                                }
                              }}
                            >
                              저장
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-shrink-0"
                              style={{ backgroundColor: '#868e96', height: '30px', fontSize: '11px' }}
                              onClick={() => { setAddingFileCommentId(null); setAdditionalFiles([]); }}
                            >
                              취소
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 첨부파일 영역 (PMS 100% 동일) */}
                    <div className="d-flex flex-column">
                      {(() => {
                        const fileItems = group.items.filter(item => checkIsFile(item));
                        const originalFiles = fileItems.filter(item => !item.file?.created_at || new Date(item.file.created_at).getTime() - new Date(group.created_at).getTime() <= 60000);
                        const addedFiles = fileItems.filter(item => item.file?.created_at && new Date(item.file.created_at).getTime() - new Date(group.created_at).getTime() > 60000);
                        addedFiles.sort((a, b) => new Date(b.file.created_at) - new Date(a.file.created_at));
                        const sortedFileItems = [...originalFiles, ...addedFiles];

                        return sortedFileItems.map((item, fIdx) => {
                          const isAdded = item.file?.created_at && new Date(item.file.created_at).getTime() - new Date(group.created_at).getTime() > 60000;

                          return (
                            <React.Fragment key={`file-wrap-${item.id}`}>
                              <div className={`d-flex align-items-center action-container px-2 py-1 ${isAdded ? 'bg-light border-top border-white' : `bg-primary bg-opacity-10 ${fIdx === 0 ? 'border-top border-primary border-opacity-10' : 'border-top border-white border-opacity-50'}`}`}>
                                {isAdded && <i className="fas fa-level-up-alt fa-rotate-90 text-secondary ms-2 me-3 fs-6 opacity-75" />}
                                <div className={`d-flex align-items-center justify-content-center ${isAdded ? 'bg-transparent text-primary' : 'bg-white rounded-circle shadow-sm'} me-2 flex-shrink-0`} style={{ width: '28px', height: '28px' }}>
                                  <i className={`${getFileIconClass(stripUniqueKey(item.file?.originalName || item.original_file_name || item.content))} ${isAdded ? 'fs-5' : 'fs-6'}`} />
                                </div>
                                <span
                                  className={`fs-7 fw-bold me-2 text-truncate flex-grow-0 ${isAdded ? 'text-dark' : 'text-primary'}`}
                                  style={{ maxWidth: 'calc(min(350px, 20vw))', cursor: 'pointer' }}
                                  onClick={() => handleDownloadFile(item.file)}
                                  onMouseEnter={(e) => {
                                    if (e.target.offsetWidth < e.target.scrollWidth) {
                                      e.target.title = stripUniqueKey(item.content);
                                    } else {
                                      e.target.title = '';
                                    }
                                  }}
                                >
                                  {stripUniqueKey(item.file?.originalName || item.original_file_name || item.content)}
                                </span>
                                <span className={`fs-8 opacity-75 flex-shrink-0 ${isAdded ? 'text-secondary' : 'text-primary'}`}>
                                  [{Math.round((item.file?.file_size || item.file_size || 1024) / 1024)}kb]
                                </span>

                                <div className="d-flex gap-1 align-items-center opacity-0 action-buttons transition-opacity ms-auto flex-shrink-0">
                                  <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="다운로드" onClick={() => handleDownloadFile(item.file)}>
                                    <i className="fas fa-download" />
                                  </button>
                                  {!isAdded && (
                                    <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="답글(파일첨부)" onClick={() => setReplyTargetId(item.id)}>
                                      <i className="fas fa-plus" />
                                    </button>
                                  )}
                                  {(currentUser?.id === item.user_id || currentUser?.id === workOrder?.author_id || currentUser?.role === 'admin' || currentUser?.isAdmin) && (
                                    <button className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="삭제" onClick={() => handleDeleteComment && handleDeleteComment(item.id)}>
                                      <i className="fas fa-trash-alt" />
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* 개별 파일 바로 밑에 붙는 답글 폼 및 하위 첨부파일 트리 */}
                              {(replyTargetId === item.id || discussionComments.some(c => c.parent_id === item.id)) && (
                                <div className="bg-light border-top border-secondary border-opacity-10 d-flex flex-column py-1">
                                  {replyTargetId === item.id && (
                                    <div className="mt-1 mx-3 mb-1 d-flex align-items-center gap-2 animation-fade-in">
                                      <i className="fas fa-level-up-alt fa-rotate-90 text-secondary ms-1 fs-5" />
                                      <div className="d-flex bg-white border flex-grow-1 align-items-center file-container position-relative" style={{ height: '32px', minWidth: 0 }}>
                                        <div className="form-control border-0 shadow-none h-100 flex-grow-1 rounded-0 fs-8 px-2 bg-white d-flex align-items-center cursor-pointer" onClick={() => replyFileInputRef.current?.click()} style={{ minWidth: 0 }}>
                                          <span className={replyFiles.length > 0 ? "text-dark text-truncate fw-bold" : "text-muted text-truncate"}>
                                            {replyFiles.length > 0 ? `${replyFiles[0].name}${replyFiles.length > 1 ? ` 외 ${replyFiles.length - 1}개` : ''}` : "첨부할 파일을 선택하세요..."}
                                          </span>
                                        </div>
                                        {replyFiles.length > 0 && (
                                          <i className="fas fa-times text-muted px-2 border-start d-flex align-items-center justify-content-center h-100 flex-shrink-0 cursor-pointer" onClick={() => setReplyFiles([])} title="전체 삭제" />
                                        )}
                                        <input type="file" className="d-none" ref={replyFileInputRef} multiple onChange={(e) => setReplyFiles(Array.from(e.target.files))} />
                                        <button className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center flex-shrink-0" title="파일 첨부" onClick={() => replyFileInputRef.current?.click()}>
                                          <i className="fas fa-upload" />
                                        </button>
                                      </div>
                                      <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-shrink-0" style={{ backgroundColor: '#4c6ef5', height: '32px' }}
                                        onClick={() => {
                                          if (!validateAttachmentFiles(replyFiles, item.id)) return;
                                           if (handleReplySubmit) {
                                            handleReplySubmit(item.id, replyContent, replyFiles, authorName);
                                          }
                                          setReplyFiles([]);
                                          setReplyContent('');
                                          setReplyTargetId(null);
                                        }}
                                      >
                                        저장
                                      </button>
                                      <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-shrink-0" style={{ backgroundColor: '#868e96', height: '32px' }}
                                        onClick={() => {
                                          setReplyTargetId(null);
                                          setReplyFiles([]);
                                          setReplyContent('');
                                        }}
                                      >
                                        취소
                                      </button>
                                    </div>
                                  )}
                                  {discussionComments.some(c => c.parent_id === item.id) && (
                                    renderReplies(item.id, group.user_id || group.user?.id, 1)
                                  )}
                                </div>
                              )}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* To-Do 등록 팝업 레이어 */}
      {todoAnchorEl && todoSource && (
        <TodoRegisterLayer
          source={todoSource}
          anchorRef={todoAnchorEl}
          onClose={() => {
            setTodoAnchorEl(null);
            setTodoSource(null);
          }}
          direction="down"
          align="right"
        />
      )}

      {/* 다중 파일 프리뷰 팝업 포털 (PMS 동일) */}
      {renderPortalPopup()}
    </div>
  );
}
